import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Package, Download, AlertTriangle, TrendingUp } from 'lucide-react'

interface DashboardStats {
  summary: {
    total_apps: number
    total_versions: number
    total_downloads: number
    total_crashes: number
    crash_rate: number
  }
  top_apps: { app_id: string; name: string; downloads: number }[]
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await fetch('/api/analytics/dashboard')
      return res.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  const statCards = [
    {
      label: 'Total Apps',
      value: stats?.summary.total_apps || 0,
      icon: Package,
      color: 'bg-blue-500',
    },
    {
      label: 'Total Downloads',
      value: stats?.summary.total_downloads || 0,
      icon: Download,
      color: 'bg-green-500',
    },
    {
      label: 'Total Versions',
      value: stats?.summary.total_versions || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      label: 'Crash Rate',
      value: `${(stats?.summary.crash_rate || 0).toFixed(2)}%`,
      icon: AlertTriangle,
      color: 'bg-orange-500',
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Dashboard</h2>
        <p className="text-gray-400">Overview of your app distribution platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10"
          >
            <div className="flex items-center gap-2 md:gap-4">
              <div className={`${stat.color} p-2 md:p-3 rounded-lg flex-shrink-0`}>
                <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-gray-400 text-xs md:text-sm truncate">{stat.label}</p>
                <p className="text-xl md:text-2xl font-bold">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top Apps */}
      <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
        <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Top Apps by Downloads</h3>
        {stats?.top_apps && stats.top_apps.length > 0 ? (
          <div className="space-y-3">
            {stats.top_apps.map((app, idx) => (
              <Link
                key={app.app_id}
                to={`/apps/${app.app_id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 w-6">{idx + 1}.</span>
                  <span className="font-medium">{app.name || app.app_id}</span>
                </div>
                <span className="text-primary-500">{app.downloads} downloads</span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No download data yet</p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/apps"
          className="bg-primary-500 hover:bg-primary-600 rounded-xl p-6 transition-colors"
        >
          <Package className="w-8 h-8 mb-2" />
          <h3 className="font-semibold">Browse Apps</h3>
          <p className="text-sm text-primary-200">View all available applications</p>
        </Link>
        <Link
          to="/analytics"
          className="bg-white/5 hover:bg-white/10 rounded-xl p-6 border border-white/10 transition-colors"
        >
          <TrendingUp className="w-8 h-8 mb-2" />
          <h3 className="font-semibold">View Analytics</h3>
          <p className="text-sm text-gray-400">Detailed download statistics</p>
        </Link>
      </div>
    </div>
  )
}
